<script>
import Vue from 'vue'
import { MODAL } from '@constants/Text'
import { GlobalBus } from '@services/bus'
import { CURRENT_RELEASE } from '@modules/global/constants/CurrentRelease'
import {
  markReleaseSeen,
  remindReleaseLater,
} from '@modules/global/utils/release-modal-storage'

// Modal de anúncio de release/novidades, dirigido 100% por um objeto
// `release` (version, badge, title, subtitle, image, features[], actionLabel).
// Cada item de `features` é um slide do carrossel: badge/title/subtitle ficam
// fixos no topo, e a imagem + os cards (feature.cards[], 1 ou mais) trocam a
// cada "Próximo" (ou clique numa bolinha). Pra anunciar o PRÓXIMO release,
// edite apenas @modules/global/constants/CurrentRelease.js (ou emita
// GlobalBus.$emit(MODAL.RELEASE_ANNOUNCEMENT, releaseCustom) com outro
// objeto) — este componente não muda.
export default {
  name: 'ReleaseAnnouncement',
  data() {
    return {
      busModal: new Vue(),
      release: { ...CURRENT_RELEASE },
      currentSlide: 0,
      // Marca que o próximo fechamento é um adiamento ("Ver mais tarde"), e não
      // uma dispensa definitiva. Ver persistDismissal.
      pendingRemindLater: false,
    }
  },
  computed: {
    slides() {
      return (this.release && this.release.features) || []
    },
    hasMultipleSlides() {
      return this.slides.length > 1
    },
    currentFeature() {
      return this.slides[this.currentSlide] || {}
    },
    currentCards() {
      return this.currentFeature.cards || []
    },
    currentImage() {
      return this.currentFeature.image || this.release.image
    },
    isLastSlide() {
      return this.currentSlide >= this.slides.length - 1
    },
    actionLabel() {
      if (!this.isLastSlide) return 'Próximo'
      return this.release.actionLabel || 'Entendido'
    },
  },
  watch: {
    // O BaseModal fecha o dialog sozinho quando a rota muda (fix de overlay
    // órfão do Vuetify — ver _base-modal.vue). Esse fechamento NÃO é ação do
    // usuário e não pode consumir um anúncio de exibição única: o app monta
    // fora do router.onReady, então a navegação inicial pode confirmar depois
    // do modal abrir e o anúncio sumiria sem nunca ter sido lido. Vira
    // adiamento, e ele volta no dia seguinte.
    //
    // Depende da ordem do scheduler do Vue 2: watchers drenam por id crescente
    // e este componente é criado antes do BaseModal filho, então a flag já está
    // de pé quando o 'close' chega. O teste cobre essa ordem.
    $route() {
      if (this.release && this.release.version) {
        this.pendingRemindLater = true
      }
    },
  },
  created() {
    GlobalBus.$on(MODAL.RELEASE_ANNOUNCEMENT, this.open)
    // O BaseModal emite 'close' no bus em QUALQUER fechamento do v-dialog —
    // inclusive ESC e clique fora, que não passam por método nenhum daqui.
    // É o único ponto que enxerga todas as saídas (ver _base-modal.vue).
    this.busModal.$on('close', this.persistDismissal)
  },
  beforeDestroy() {
    GlobalBus.$off(MODAL.RELEASE_ANNOUNCEMENT, this.open)
    this.busModal.$off('close', this.persistDismissal)
  },
  methods: {
    clear() {
      this.release = { ...CURRENT_RELEASE }
      this.currentSlide = 0
      this.pendingRemindLater = false
    },
    open(release = {}) {
      this.clear()
      this.release = { ...CURRENT_RELEASE, ...release }
      this.busModal.$emit('dialog', true)
    },
    goToSlide(index) {
      this.currentSlide = index
    },
    next() {
      if (this.isLastSlide) {
        this.close()
        return
      }
      this.currentSlide += 1
    },
    // O anúncio é de uma vez só: encerrar de QUALQUER forma — X, ESC, clique
    // fora, "Entendido", "Não mostrar novamente" — marca a version como vista e
    // ele não volta. A única saída que preserva o anúncio é "Ver mais tarde".
    persistDismissal() {
      const version = this.release && this.release.version
      if (!version) return

      if (this.pendingRemindLater) {
        this.pendingRemindLater = false
        remindReleaseLater(version)
        return
      }

      markReleaseSeen(version)
    },
    remindLater() {
      this.pendingRemindLater = true
      this.close()
    },
    close() {
      this.busModal.$emit('dialog', false)
    },
  },
}
</script>

<template>
  <BaseModal
    :bus="busModal"
    centerScreen
    centerAndDecreasesPadding
    maxWidth="960px"
    backgroundColor="var(--dark)"
    withoutCloseButton
    paddingOverride="0"
    titleRowPaddingOverride="0"
  >
    <div class="release-announcement position-relative">
      <v-btn
        icon
        small
        class="release-announcement__close"
        aria-label="Fechar"
        @click="close"
      >
        <icon-svg name="close-circle" :size="24" color="secondary" />
      </v-btn>

      <div class="release-announcement__body">
        <div class="release-announcement__content d-flex flex-column pa-6">
          <span
            v-if="release.badge"
            class="az-ds-label release-announcement__badge mb-2"
          >
            {{ release.badge }}
          </span>
          <h2 class="az-title-secondary release-announcement__title mb-2">
            {{ release.title }}
          </h2>
          <p
            v-if="release.subtitle"
            class="az-ds-body release-announcement__subtitle mb-4"
          >
            {{ release.subtitle }}
          </p>

          <transition name="release-announcement-fade" mode="out-in">
            <div
              v-if="currentCards.length"
              :key="'cards-' + currentSlide"
              class="release-announcement__cards d-flex flex-column az-gap-small"
            >
              <div
                v-for="(card, index) in currentCards"
                :key="'card-' + currentSlide + '-' + index"
                class="release-announcement__feature d-flex align-items-start az-gap-small pa-3"
              >
                <div
                  v-if="card.icon"
                  class="release-announcement__feature-icon d-flex align-center justify-center flex-shrink-0"
                >
                  <icon-svg :name="card.icon" :size="18" color="primary" />
                </div>
                <div class="flex-grow-1">
                  <h3
                    class="az-ds-card-title release-announcement__feature-title mb-1"
                  >
                    {{ card.title }}
                  </h3>
                  <p
                    class="az-ds-body release-announcement__feature-description mb-0"
                  >
                    {{ card.description }}
                  </p>
                </div>
              </div>
            </div>
          </transition>
        </div>

        <transition name="release-announcement-fade" mode="out-in">
          <div
            v-if="currentImage"
            :key="'media-' + currentSlide"
            class="release-announcement__media"
          >
            <img :src="currentImage" :alt="release.title" />
          </div>
        </transition>
      </div>

      <div
        class="release-announcement__actions d-flex align-center justify-space-between px-6 py-4"
      >
        <div class="release-announcement__dots">
          <template v-if="hasMultipleSlides">
            <button
              v-for="(slide, index) in slides"
              :key="'dot-' + index"
              type="button"
              class="release-announcement__dot"
              :class="{
                'release-announcement__dot--active': index === currentSlide,
              }"
              :aria-label="'Ir para o slide ' + (index + 1)"
              @click="goToSlide(index)"
            />
          </template>
        </div>
        <div class="d-flex align-items-center az-gap-small">
          <BaseButton
            underlined
            minWidth="auto"
            class="release-announcement__remind-later"
            @click="remindLater"
          >
            Ver mais tarde
          </BaseButton>
          <BaseButton primary outline @click="close">
            Não mostrar novamente
          </BaseButton>
          <BaseButton primary @click="next">
            {{ actionLabel }}
          </BaseButton>
        </div>
      </div>
    </div>
  </BaseModal>
</template>

<style lang="sass" scoped>
// Único modal dark-mode do app — layout/espaçamento via utilitários Vuetify
// (d-flex, flex-md-row, pa-*, mb-*) e tipografia via az-ds-*/az-title-* do DS
// (tamanho/peso/font-family reaproveitados). Cor é a ÚNICA coisa sobrescrita
// aqui: az-ds-*/az-title-* assumem texto escuro sobre fundo claro, e este é o
// primeiro modal dark-mode do app — não existe ainda um token de texto
// "on-dark". Cor de marca (ícones/badge/botão/bolinha ativa) sempre via
// var(--primary), a cor institucional dark sempre via var(--dark) — nunca
// hardcoded.
.release-announcement
  display: flex
  flex-direction: column

  // v-btn icon já cuida do formato circular, centralização e ripple — só
  // posiciona sobre a imagem. Fundo branco sólido (padrão de ícone de ação):
  // mesmo tratamento visual da imagem clara à direita.
  &__close
    position: absolute
    top: 16px
    right: 16px
    z-index: 2
    background: var(--white) !important

    &:hover
      background: var(--gray-1) !important

  // Linha (texto + imagem) e footer (bolinhas + botão) — dois blocos
  // empilhados, o footer sempre ocupando a largura TOTAL do modal (por isso
  // é irmão de __body, não filho de __content).
  &__body
    display: flex
    flex-direction: column
    flex: 1 1 auto

  &__content
    width: 100%
    flex: 1 1 auto

  &__badge
    align-self: flex-start
    display: inline-block
    padding: 4px 10px
    border-radius: 4px
    border: 1px solid var(--primary)
    color: var(--primary) !important

  &__title
    color: #fff !important

  &__subtitle
    color: rgba(255, 255, 255, 0.7) !important

  &__feature
    border-radius: 10px
    background: rgba(255, 255, 255, 0.04)
    border: 1px solid rgba(255, 255, 255, 0.08)

  &__feature-title
    color: #fff !important

  &__feature-description
    color: rgba(255, 255, 255, 0.65) !important

  &__feature-icon
    width: 36px
    height: 36px
    border-radius: 50%
    background: rgba(255, 255, 255, 0.08)

  // Footer — sempre a largura TOTAL do modal (imagem + texto), por isso a
  // borda divisória some sob a coluna de imagem também, não só sob o texto.
  &__actions
    border-top: 1px solid rgba(255, 255, 255, 0.1)
    flex-shrink: 0

  // "Ver mais tarde" é a ação de menor peso do rodapé (btn-underlined), e essa
  // variante herda a cor de texto padrão — escura, invisível sobre var(--dark).
  // Mesmo tratamento on-dark do resto do modal.
  &__remind-later
    color: rgba(255, 255, 255, 0.7) !important

    &:hover
      color: var(--white) !important

  &__dots
    display: flex
    align-items: center
    gap: 8px
    min-height: 8px

  &__dot
    width: 8px
    height: 8px
    padding: 0
    border: none
    border-radius: 50%
    background: rgba(255, 255, 255, 0.24)
    cursor: pointer
    transition: background-color 0.2s ease, transform 0.2s ease

    &:hover
      background: rgba(255, 255, 255, 0.4)

    &--active
      background: var(--primary)
      transform: scale(1.2)

  &__media
    display: none

  @media (min-width: 640px)
    &__body
      flex-direction: row
      // Sem stretch: a imagem tem proporção fixa própria (aspect-ratio
      // abaixo) e não deve esticar pra igualar a altura do texto — isso é
      // o que deixava sobrando barra vazia em cima/embaixo do gif.
      align-items: flex-start

    &__content
      flex: 0 0 35%
      max-width: 35%

    &__media
      flex: 0 0 65%
      max-width: 65%
      display: block
      position: relative
      overflow: hidden
      background: rgba(255, 255, 255, 0.02)
      // Mesma proporção dos gifs de release (900x609) — width já é fixa
      // (65%), então isso deriva a height certa sem sobrar/faltar espaço.
      aspect-ratio: 900 / 609

      img
        width: 100%
        height: 100%
        object-fit: contain
        object-position: center

// Transição suave entre slides (imagem e card de feature) — troca de
// conteúdo nunca deve parecer um "corte seco" (princípio de UX do projeto).
.release-announcement-fade-enter-active,
.release-announcement-fade-leave-active
  transition: opacity 0.2s ease

.release-announcement-fade-enter,
.release-announcement-fade-leave-to
  opacity: 0
</style>
